import { auth } from '../firebaseConfig';

export type LocalizedString = {
  en: string;
  es: string;
};

export type ResourceType = string; // Ahora es un ID dinámico de la colección resourceTypes

export interface ResourceTypeEntity {
  id: string;
  name: LocalizedString;
  resourceCount: number;
}

export interface CreateResourceTypeInput {
  name: LocalizedString;
}

export type UpdateResourceTypeInput = Partial<CreateResourceTypeInput> & { id: string };

export interface Resource {
  id: string;
  title: LocalizedString;
  description: LocalizedString;
  url: string;
  typeId: string; // Cambiado de 'type' a 'typeId' para mayor claridad
  categoryId: string;
  tags: string[];
  createdAt: unknown;
  createdBy: string;
}

export interface Category {
  id: string;
  name: LocalizedString;
  resourceCount: number;
}

export interface CreateResourceInput {
  title: LocalizedString;
  description: LocalizedString;
  url: string;
  typeId: string; // Cambiado de 'type' a 'typeId' para mayor claridad
  categoryId: string;
  tags?: string[];
}

export type UpdateResourceInput = Partial<CreateResourceInput> & { id: string };

export interface CreateCategoryInput {
  name: LocalizedString;
}

export type UpdateCategoryInput = Partial<CreateCategoryInput> & { id: string };

export interface PaginatedResponse<T> {
  resources: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    offset: number;
  };
  filters: {
    search: string | null;
    categoryId: string | null;
  };
}

export type ResourcesResponse = PaginatedResponse<Resource>;

function normalizeBaseUrl(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

function getApiBaseUrl(): string {
  const explicitBase = (
    import.meta as unknown as { env: Record<string, unknown> }
  ).env.VITE_API_BASE_URL as string | undefined;
  if (explicitBase && explicitBase.length > 0) {
    return normalizeBaseUrl(explicitBase);
  }
  const projectId = (import.meta as unknown as { env: Record<string, unknown> })
    .env.VITE_FIREBASE_PROJECT_ID as string;
  const useEmulators =
    ((import.meta as unknown as { env: Record<string, unknown> }).env
      .VITE_USE_EMULATORS as string) === 'true';
  if (!projectId) throw new Error('VITE_FIREBASE_PROJECT_ID no está definido');
  if (useEmulators) {
    return `http://127.0.0.1:5001/${projectId}/us-central1/api`;
  }
  return `https://us-central1-${projectId}.cloudfunctions.net/api`;
}

async function getAuthToken(): Promise<string | undefined> {
  const user = auth.currentUser;
  if (!user) return undefined;
  return user.getIdToken();
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const base = getApiBaseUrl();
  const token = await getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(init?.headers || {}),
  };
  if (token)
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;

  const res = await fetch(`${base}${path}`, {
    ...init,
    headers,
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = (await res.json()) as unknown as { error?: string };
      if (data?.error) message = data.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

export async function getCategories(): Promise<Category[]> {
  return request<Category[]>('/categories', { method: 'GET' });
}

export async function addCategory(
  data: CreateCategoryInput
): Promise<Category> {
  return request<Category>('/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCategory(
  data: UpdateCategoryInput
): Promise<Category> {
  const { id, ...updates } = data;
  return request<Category>(`/categories/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteCategory(id: string): Promise<void> {
  await request<void>(`/categories/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function getResources(
  searchTerm?: string,
  categoryId?: string,
  page: number = 1,
  limit: number = 20
): Promise<ResourcesResponse> {
  const params = new URLSearchParams();
  if (searchTerm && searchTerm.trim()) {
    params.append('search', searchTerm.trim());
  }
  if (categoryId && categoryId !== 'all') {
    params.append('categoryId', categoryId);
  }
  params.append('page', page.toString());
  params.append('limit', limit.toString());
  
  const queryString = params.toString();
  const path = `/resources?${queryString}`;
  
  return request<ResourcesResponse>(path, { method: 'GET' });
}

export async function addResource(
  data: CreateResourceInput
): Promise<Resource> {
  return request<Resource>('/resources', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateResource(
  data: UpdateResourceInput
): Promise<Resource> {
  const { id, ...updates } = data;
  return request<Resource>(`/resources/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteResource(id: string): Promise<void> {
  await request<void>(`/resources/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// Funciones para tipos de recursos
export async function getResourceTypes(): Promise<ResourceTypeEntity[]> {
  return request<ResourceTypeEntity[]>('/resource-types', { method: 'GET' });
}

export async function addResourceType(
  data: CreateResourceTypeInput
): Promise<ResourceTypeEntity> {
  return request<ResourceTypeEntity>('/resource-types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateResourceType(
  data: UpdateResourceTypeInput
): Promise<ResourceTypeEntity> {
  const { id, ...updates } = data;
  return request<ResourceTypeEntity>(`/resource-types/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteResourceType(id: string): Promise<void> {
  await request<void>(`/resource-types/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
